/**
 * A helper class that is used handling <a> tags
 *
 * It has custom URL logic for mobile such as the ability to
 * detect URLs that have been tagged as explicitly for mobile
 * */
const UrlHelper = function () {
  const self = this;

  function isBooleanAttributeSet($elem, attrName) {
    const attrVal = $elem.attr(attrName);

    // need to account for both an explicit setting of true
    // and if they set as just '<a data-external></a>' without
    // a value (to adhere to HTML boolean attribute spec)
    return (
      typeof attrVal !== 'undefined' &&
      (attrVal.toLowerCase() === 'true' || attrVal.length === 0)
    );
  }

  /**
   * NAVIGATION: for navigating to a page the mobile app
   * DEEPLINK: for opening up screens in other mobile apps (
   *           i.e phone #s for dialer app, mailto links for
   *           mail app, etc.)
   * BROWSER: for opening in the native browser app such as
   *          to download attachments
   * METADATA: to send state events like loading progress
   * */
  self.handlers = {
    NAVIGATION: 'navigate',
    DEEPLINK: 'deepLink',
    BROWSER: 'browser',
    METADATA: 'metadata',
  };

  self.isMailToLink = function (href) {
    return href.startsWith('mailto:');
  };

  self.isPhoneLink = function (href) {
    return href.startsWith('tel:');
  };

  self.isPlaceholderLink = function (href) {
    return !(href && href.length > 0);
  };

  self.isAbsoluteURL = function (str) {
    return /^(?:[a-z]+:)?\/\//i.test(str);
  };

  /**
   * The data-attribute is always accessed by the 'attr' and not 'data'
   * because we only care for the initial value it was set to on load
   *
   * This is because the $.data() initially retrieves
   * the value from the value from the HTML attribute and then
   * just saves it to an internal JS variable inside jQuery
   * and uses that from then on
   * */
  self.isAllowlistedLink = function ($elem) {
    return isBooleanAttributeSet($elem, 'data-for-mobile');
  };

  self.isMarkedExternal = function ($elem) {
    return isBooleanAttributeSet($elem, 'data-external');
  };

  /**
   * Is the link marked as a link meant to open up a specific
   * native (non-web) screen in the mobile app?
   * */
  self.isNativeScreenLink = function ($elem) {
    return isBooleanAttributeSet($elem, 'data-for-native-screen');
  };

  self.isExternalURL = function ($elem) {
    const str = $elem.attr('href');

    if (self.isMarkedExternal($elem)) {
      return true;
    } else if (!self.isAbsoluteURL(str)) {
      return false;
    }

    return str.toLowerCase().indexOf(window.location.host) < 0;
  };

  self.getLinkType = function ($elem) {
    const href = $elem.attr('href') || '';

    if (self.isMailToLink(href)) {
      return 'email';
    } else if (self.isPhoneLink(href)) {
      return 'phone';
    } else if (self.isExternalURL($elem)) {
      return self.handlers.BROWSER;
    } else {
      return self.handlers.NAVIGATION;
    }
  };

  /**
   * NOTE: This only converts relative urls
   * not invalid URLs like `linkedin.com` being
   * converted over to a valid url. For that,
   *
   * @see forceToValidAbsoluteURL
   * */
  self.toAbsoluteURL = function (url) {
    if (self.isAbsoluteURL(url)) {
      return url;
    }

    if (url.indexOf('/') === 0) {
      return window.location.origin + url;
    } else {
      // for urls relative to current page path
      const currentParentURL = window.location.href.slice(
        0,
        window.location.href.lastIndexOf('/') + 1
      );
      return currentParentURL + url;
    }
  };

  /**
   * The implementation is pretty dumb by most
   * standards so this should only be used as a last resort
   * */
  self.forceToAbsoluteURL = function (invalidURL) {
    if (!self.isAbsoluteURL(invalidURL)) {
      return 'http://' + invalidURL;
    }

    return invalidURL;
  };

  /**
   * Gets a consistent data structure used by both mobile
   * libraries to convey to their respective devices
   *
   * @param {String} href - the url
   * @param {self.HANDLERS} type - the type of handler
   * @param [title] title - the title label for the new screen that will be opened
   * @param [boolean] isForNativeScreen - is this intended to open a native screen?
   * */
  self.getNormalizedData = function (href, type, title, isForNativeScreen) {
    // force invalid URLs like `linkedin.com` which
    // is technically an invalid URL by browser spec
    // to be valid before handing off to iOS
    if (type === self.handlers.BROWSER) {
      href = self.forceToAbsoluteURL(href);
    }

    if (type === self.handlers.NAVIGATION) {
      // find out whether it is for navigation to a webview or to a native screen.
      // This will determine what to send.
      if (isForNativeScreen) {
        /**
         * for deeplinking to native screens, don't send full URL
         * instead, send the scheme and prepend a double `//`
         * but WITHOUT the authority. This is because the scheme automatically
         * indicates the authority and thus is redundant.
         *
         * url = Greenhouse URL
         *
         * This allows us to differentiate between links to WebView pages and
         * native pages and maintains feature parity with the notifications for mobile
         *
         * Refer to the RFC here for details:
         *
         * @see https://tools.ietf.org/html/rfc3986#section-3
         * @see https://tools.ietf.org/html/rfc3986#section-3.2
         * @see https://tools.ietf.org/html/rfc3986#page-6
         * */
        return {
          handler: type,
          url: 'greenhouse://' + href,
        };
      } else {
        // if its for navigation, iOS requires the full
        // absolute URL as well as a title to show in
        // the header
        return {
          handler: type,
          href: self.toAbsoluteURL(href),
          title: title,
        };
      }
    } else {
      return {
        handler: self.handlers.DEEPLINK,
        href: href,
        type: type,
      };
    }
  };

  self.getNormalizedDataFromLink = function ($elem) {
    const href = $elem.attr('href');
    const title = $elem.attr('title');
    const type = self.getLinkType($elem);
    const isForNativeScreen = self.isNativeScreenLink($elem);

    return self.getNormalizedData(href, type, title, isForNativeScreen);
  };
};

export default UrlHelper;
